import type { RealtimeChannel } from '@supabase/supabase-js';
import type { GameMode, Participant, Room } from '../types';
import { getSupabase } from './supabase';
import { uuid, generateRoomCode } from '../utils/helpers';

export type RoomEvent =
  | { type: 'participants'; participants: Participant[] }
  | { type: 'game_start'; mode: GameMode; names: string[] }
  | { type: 'game_result'; payload: unknown }
  | { type: 'kicked'; id: string }
  | { type: 'host_left' };

type RoomListener = (event: RoomEvent) => void;

interface PresenceState {
  id: string;
  name: string;
  isHost: boolean;
  joinedAt: number;
}

export class RoomManager {
  private channel: RealtimeChannel | null = null;
  private myId: string = uuid();
  private myName = '';
  private isHost = false;
  private code = '';
  private listeners: RoomListener[] = [];
  private hasSeenHost = false;

  onEvent(listener: RoomListener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(event: RoomEvent) {
    this.listeners.forEach((l) => l(event));
  }

  async createRoom(hostName: string, mode: GameMode): Promise<string> {
    const sb = getSupabase();
    this.myName = hostName;
    this.isHost = true;
    this.code = generateRoomCode();

    this.channel = sb.channel(`room:${this.code}`, {
      config: { presence: { key: this.myId } },
    });

    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel!.presenceState<PresenceState>();
        const participants: Participant[] = Object.values(state)
          .flat()
          .map((p) => ({ id: p.id, name: p.name, isHost: p.isHost, joinedAt: p.joinedAt }));
        this.emit({ type: 'participants', participants });
      })
      .on('broadcast', { event: 'kicked' }, ({ payload }) => {
        if (payload.id === this.myId) {
          this.emit({ type: 'kicked', id: this.myId });
          this.leave();
        }
      })
      .on('broadcast', { event: 'game_start' }, ({ payload }) => {
        this.emit({ type: 'game_start', mode: payload.mode, names: payload.names });
      })
      .on('broadcast', { event: 'game_result' }, ({ payload }) => {
        this.emit({ type: 'game_result', payload });
      });

    await new Promise<void>((resolve, reject) => {
      this.channel!.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await this.channel!.track({
            id: this.myId,
            name: hostName,
            isHost: true,
            joinedAt: Date.now(),
          } satisfies PresenceState);
          resolve();
        } else if (status === 'CHANNEL_ERROR') {
          reject(new Error('채널 연결 실패'));
        }
      });
    });

    return this.code;
  }

  async joinRoom(code: string, name: string): Promise<void> {
    const sb = getSupabase();
    this.myName = name;
    this.isHost = false;
    this.code = code.toUpperCase();

    this.channel = sb.channel(`room:${this.code}`, {
      config: { presence: { key: this.myId } },
    });

    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel!.presenceState<PresenceState>();
        const participants: Participant[] = Object.values(state)
          .flat()
          .map((p) => ({ id: p.id, name: p.name, isHost: p.isHost, joinedAt: p.joinedAt }));
        this.emit({ type: 'participants', participants });

        // Host가 없으면 host_left 이벤트
        const hasHost = participants.some((p) => p.isHost);
        if (hasHost) this.hasSeenHost = true;
        if (!hasHost && this.hasSeenHost && participants.length > 0) {
          this.emit({ type: 'host_left' });
        }
      })
      .on('broadcast', { event: 'kicked' }, ({ payload }) => {
        if (payload.id === this.myId) {
          this.emit({ type: 'kicked', id: this.myId });
          this.leave();
        }
      })
      .on('broadcast', { event: 'game_start' }, ({ payload }) => {
        this.emit({ type: 'game_start', mode: payload.mode, names: payload.names });
      })
      .on('broadcast', { event: 'game_result' }, ({ payload }) => {
        this.emit({ type: 'game_result', payload });
      });

    await new Promise<void>((resolve, reject) => {
      this.channel!.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await this.channel!.track({
            id: this.myId,
            name,
            isHost: false,
            joinedAt: Date.now(),
          } satisfies PresenceState);
          resolve();
        } else if (status === 'CHANNEL_ERROR') {
          reject(new Error('방을 찾을 수 없어요. 방 코드를 확인해주세요.'));
        }
      });
    });
  }

  async startGame(mode: GameMode, names: string[]): Promise<void> {
    if (!this.channel || !this.isHost) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'game_start',
      payload: { mode, names },
    });
  }

  async broadcastResult(payload: unknown): Promise<void> {
    if (!this.channel || !this.isHost) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'game_result',
      payload,
    });
  }

  async kickParticipant(id: string): Promise<void> {
    if (!this.channel || !this.isHost) return;
    await this.channel.send({
      type: 'broadcast',
      event: 'kicked',
      payload: { id },
    });
  }

  async leave(): Promise<void> {
    if (this.channel) {
      await this.channel.untrack();
      await getSupabase().removeChannel(this.channel);
      this.channel = null;
    }
  }

  getCode() { return this.code; }
  getMyId() { return this.myId; }
  getIsHost() { return this.isHost; }
  getMyName() { return this.myName; }
}
